import {useSyncExternalStore} from 'react'

export interface ViewportInformation {
  height?: number
  isLandscape?: boolean
  isTouchDevice?: boolean
  pixelDensity?: number
  width?: number
}

const SERVER_SNAPSHOT: ViewportInformation = {}

class ViewportStore {
  #snapshot: ViewportInformation = {}
  #initialized = false
  #listeners = new Set<() => void>()
  #deferredReadId: number | null = null

  #readViewport(): ViewportInformation {
    // Read viewport dimensions from documentElement
    const clientWidth = document.documentElement.clientWidth
    const clientHeight = document.documentElement.clientHeight

    // On mobile browsers, during initial page load, clientWidth can be 0 or very small
    // before the viewport is fully established. Use window.innerWidth as a fallback
    // to prevent flashing from a narrow to proper width.
    const width = clientWidth > 0 ? clientWidth : window.innerWidth
    const height = clientHeight > 0 ? clientHeight : window.innerHeight

    return {
      height,
      isLandscape: window.matchMedia('(orientation: landscape)').matches,
      isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      pixelDensity: window.devicePixelRatio,
      width,
    }
  }

  #viewportEqual(a: ViewportInformation, b: ViewportInformation): boolean {
    return (
      a.height === b.height &&
      a.width === b.width &&
      a.isLandscape === b.isLandscape &&
      a.isTouchDevice === b.isTouchDevice &&
      a.pixelDensity === b.pixelDensity
    )
  }

  // Called from both subscribe and getSnapshot because React calls getSnapshot
  // before subscribe during initial render. The #initialized flag ensures we
  // only read the DOM once. Both call sites are necessary to handle cases where
  // getSnapshot is called without a subscription (e.g., during hydration checks).
  #initialize() {
    if (!this.#initialized && typeof document !== 'undefined') {
      this.#initialized = true
      this.#snapshot = this.#readViewport()

      // On mobile browsers, the viewport may not be fully established during the
      // first synchronous read. Schedule a deferred read after the browser has
      // completed its layout to ensure we have the correct dimensions and prevent
      // flashing from a narrow viewport to the proper width.
      if (this.#deferredReadId === null) {
        // Use requestAnimationFrame to defer until after the browser's layout pass
        this.#deferredReadId = requestAnimationFrame(() => {
          this.#deferredReadId = null
          const updatedSnapshot = this.#readViewport()
          // Only notify listeners if the viewport actually changed
          if (!this.#viewportEqual(updatedSnapshot, this.#snapshot)) {
            this.#snapshot = updatedSnapshot
            for (const listener of this.#listeners) {
              listener()
            }
          }
        })
      }
    }
  }

  #onResize = () => {
    const newSnapshot = this.#readViewport()
    if (!this.#viewportEqual(newSnapshot, this.#snapshot)) {
      this.#snapshot = newSnapshot
      for (const listener of this.#listeners) {
        listener()
      }
    }
  }

  subscribe = (callback: () => void): (() => void) => {
    this.#initialize()

    const wasEmpty = this.#listeners.size === 0
    this.#listeners.add(callback)
    if (wasEmpty) {
      // eslint-disable-next-line github/prefer-observers
      window.addEventListener('resize', this.#onResize)
      window.addEventListener('orientationchange', this.#onResize)
    }

    return () => {
      this.#listeners.delete(callback)
      if (this.#listeners.size === 0) {
        window.removeEventListener('resize', this.#onResize)
        window.removeEventListener('orientationchange', this.#onResize)
      }
    }
  }

  getSnapshot = (): ViewportInformation => {
    this.#initialize()
    return this.#snapshot
  }

  getServerSnapshot = (): ViewportInformation => {
    return SERVER_SNAPSHOT
  }

  // For testing only - resets the store to initial state
  _reset() {
    if (this.#listeners.size > 0) {
      window.removeEventListener('resize', this.#onResize)
      window.removeEventListener('orientationchange', this.#onResize)
    }
    // Cancel any pending deferred read
    if (this.#deferredReadId !== null) {
      cancelAnimationFrame(this.#deferredReadId)
      this.#deferredReadId = null
    }
    this.#snapshot = {}
    this.#initialized = false
    this.#listeners.clear()
  }
}

// Exported for testing only
export const viewportStore = new ViewportStore()

export function useViewport(): ViewportInformation {
  return useSyncExternalStore(viewportStore.subscribe, viewportStore.getSnapshot, viewportStore.getServerSnapshot)
}
